import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import WidgetReference from "../components/setup/WidgetReference";
import "./SweepstakesEntryWidgets.css";

const widgetSchema = {
  "@type": "ItemList",
  name: "Sweepstakes widget locations",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Product page entries" },
    { "@type": "ListItem", position: 2, name: "Cart entry widget" },
    { "@type": "ListItem", position: 3, name: "Checkout line item entries" },
    { "@type": "ListItem", position: 4, name: "Checkout total summary" },
    { "@type": "ListItem", position: 5, name: "Thank-you page summary" },
    { "@type": "ListItem", position: 6, name: "Customer account widget" },
    { "@type": "ListItem", position: 7, name: "Guest email lookup widget" },
  ],
};

const faqSchema = {
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What are sweepstakes entry widgets?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "Entry widgets display how many sweepstakes entries a shopper earns at key points like product pages, cart, checkout, and post-purchase.",
      },
    },
    {
      "@type": "Question",
      name: "Where should I show sweepstakes entries on Shopify?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "Show entries on product pages, in the cart, at checkout, and on the thank-you page so shoppers see the value of each purchase.",
      },
    },
    {
      "@type": "Question",
      name: "Does Clean Sweeps include entry widgets?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "Yes. Clean Sweeps provides entry widgets across product, cart, checkout, thank-you, and account experiences.",
      },
    },
  ],
};

const SweepstakesEntryWidgets = () => {
  return (
    <main className="widgets-page">
      <Seo
        title="Sweepstakes Widgets for Shopify | Clean Sweeps"
        description="Explore Clean Sweeps widgets for Shopify, with current settings, requirements, troubleshooting, and guides for adding widgets to your theme."
        path="/sweepstakes-entry-widgets"
        image="/og/sweepstakes-entry-widgets.png"
        imageAlt="Sweepstakes widgets for Shopify"
        schema={[widgetSchema, faqSchema]}
      />

      <header className="widgets-page__hero">
        <p className="widgets-page__eyebrow">Widget settings and setup</p>
        <h1>Sweepstakes widgets</h1>
        <p className="widgets-page__lead">
          Show sweepstakes entries everywhere shoppers look.{" "}
          Sweepstakes entry widgets keep customers informed, increase
          engagement, and reduce confusion. Clean Sweeps includes widgets across
          your Shopify storefront and checkout.
        </p>
        <div className="widgets-page__actions">
          <Link to="/setup?tab=entry-widgets" className="btn btn--primary">
            How to add widgets to your theme
          </Link>
          <a href="#widget-reference" className="btn btn--ghost">
            View widget settings
          </a>
          <a
            href="https://apps.shopify.com/clean-sweeps"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--primary"
          >
            Install Clean Sweeps
          </a>
          <Link to="/what-we-do" className="btn btn--ghost">
            Explore features
          </Link>
        </div>
      </header>

      <section
        id="widget-reference"
        className="widgets-page__section widgets-page__reference"
      >
        <WidgetReference guideId="all-widgets" />
      </section>

      <section className="widgets-page__section">
        <div className="widgets-page__header">
          <h2>Entry widget locations</h2>
          <p>
            Place entry totals in every high-intent spot to keep sweepstakes
            momentum high.
          </p>
        </div>
        <div className="widgets-page__grid">
          <article className="widgets-page__card">
            <h3>Product page widget</h3>
            <p>Shows entries earned for each product before adding to cart.</p>
          </article>
          <article className="widgets-page__card">
            <h3>Cart widget</h3>
            <p>Highlights entries earned and bonus thresholds in the cart.</p>
          </article>
          <article className="widgets-page__card">
            <h3>Checkout line item</h3>
            <p>Shows entries for each item during checkout.</p>
          </article>
          <article className="widgets-page__card">
            <h3>Checkout total summary</h3>
            <p>Displays total sweepstakes entries for the order.</p>
          </article>
          <article className="widgets-page__card">
            <h3>Thank-you page</h3>
            <p>Confirms entries earned after purchase.</p>
          </article>
          <article className="widgets-page__card">
            <h3>Account widget</h3>
            <p>Shows entry totals in the customer account area.</p>
          </article>
          <article className="widgets-page__card">
            <h3>Guest lookup widget</h3>
            <p>Lets guest shoppers view entries by email.</p>
          </article>
        </div>
      </section>

      <section className="widgets-page__section">
        <div className="widgets-page__header">
          <h2>Messaging tips for entry visibility</h2>
          <p>
            Consistent copy increases clarity and helps shoppers stay engaged.
          </p>
        </div>
        <ul className="widgets-page__list">
          <li>Use “entries” instead of points or credits.</li>
          <li>Highlight bonus thresholds directly in cart and checkout.</li>
          <li>Confirm totals on the thank-you page with next-step guidance.</li>
          <li>Link to official rules or AMOE instructions where relevant.</li>
        </ul>
      </section>

      <section className="widgets-page__section">
        <div className="widgets-page__header">
          <h2>Entry widget checklist</h2>
          <p>Ensure your sweepstakes app covers these essentials.</p>
        </div>
        <ul className="widgets-page__list">
          <li>Product-level entries with multipliers and bonuses.</li>
          <li>Order total entries and bonus thresholds.</li>
          <li>Post-purchase confirmation for entry totals.</li>
          <li>Customer account visibility and guest lookup.</li>
        </ul>
        <p className="widgets-page__note">
          Need help setting these up? See the{" "}
          <Link to="/setup">Setup Guides</Link>.
        </p>
      </section>

      <section className="widgets-page__section widgets-page__faq">
        <div className="widgets-page__header">
          <h2>Entry widget FAQs</h2>
          <p>Answers to common questions about sweepstakes entry widgets.</p>
        </div>
        <div className="widgets-page__faq-item">
          <h3>Do entry widgets improve sweepstakes engagement?</h3>
          <p>
            Yes. Showing entries at each step helps shoppers understand the
            value of each purchase and stay motivated.
          </p>
        </div>
        <div className="widgets-page__faq-item">
          <h3>Where should I show entries?</h3>
          <p>
            Product pages, cart, checkout, and the thank-you page are the most
            important touchpoints.
          </p>
        </div>
        <div className="widgets-page__faq-item">
          <h3>Does Clean Sweeps include all widget types?</h3>
          <p>
            Clean Sweeps includes product, cart, checkout, thank-you, account,
            and guest lookup widgets.
          </p>
        </div>
      </section>

      <section className="widgets-page__cta">
        <h2>Make sweepstakes entries visible everywhere.</h2>
        <p>
          Clean Sweeps helps Shopify merchants surface entries throughout the
          shopping experience.
        </p>
        <div className="widgets-page__actions">
          <a
            href="https://apps.shopify.com/clean-sweeps"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--primary"
          >
            Get the Shopify app
          </a>
          <Link to="/resources" className="btn btn--ghost">
            Browse resources
          </Link>
        </div>
      </section>
    </main>
  );
};

export default SweepstakesEntryWidgets;
